import * as React from "react"

import * as SliderPrimitive from "@radix-ui/react-slider"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"

type AccessibleName =
  | { "aria-label": string; "aria-labelledby"?: string }
  | { "aria-label"?: string; "aria-labelledby": string }

const sliderVariants = cva([
  "relative flex w-full touch-none select-none items-center",
  "data-[disabled]:opacity-50",
])

const sliderTrackVariants = cva([
  "relative h-1 w-full grow overflow-hidden rounded-full",
  "bg-surface-1",
])

const sliderRangeVariants = cva([
  "absolute h-full rounded-full",
  "bg-coval-contrast",
])

const sliderThumbVariants = cva([
  "block size-4 shrink-0 rounded-full",
  "not-data-[disabled]:cursor-grab not-data-[disabled]:active:cursor-grabbing",
  "border border-coval-border bg-white",
  "shadow-sm",
  "transition-colors duration-150",
  "not-data-[disabled]:hover:bg-surface-default-hover",
  "focus-visible:outline-none focus-visible:border-coval-border-active",
])

export type SliderProps = React.ComponentPropsWithoutRef<
  typeof SliderPrimitive.Root
> &
  AccessibleName &
  VariantProps<typeof sliderVariants>

const Slider = React.forwardRef<
  React.ComponentRef<typeof SliderPrimitive.Root>,
  SliderProps
>(({ className, defaultValue, ...props }, ref) => (
  <SliderPrimitive.Root
    ref={ref}
    defaultValue={defaultValue ?? [0]}
    className={cn(sliderVariants(), className)}
    {...props}
  >
    <SliderPrimitive.Track className={cn(sliderTrackVariants())}>
      <SliderPrimitive.Range className={cn(sliderRangeVariants())} />
    </SliderPrimitive.Track>
    {(props.value ?? defaultValue ?? [0]).map((_, i) => (
      <SliderPrimitive.Thumb key={i} className={cn(sliderThumbVariants())} />
    ))}
  </SliderPrimitive.Root>
))
Slider.displayName = SliderPrimitive.Root.displayName

export {
  Slider,
  sliderVariants,
  sliderTrackVariants,
  sliderRangeVariants,
  sliderThumbVariants,
}
